import React from 'react';
import { createRoot } from 'react-dom/client';
import {Provider} from 'react-redux';
import {CookiesProvider} from "react-cookie";
import TraceKit from 'tracekit';
import axios from 'axios';

import App from './App';
import CustomerService from "./services/CustomerService";
import API from "./services/API";
import {isTouchDevice} from "./util";
import {store} from "./store";
import {changeUser} from "./store/slice/auth";


// TraceKit adds the surrounding lines of source code to each stack frame. The production bundle is minified into
// very long lines (hundreds of KB) so without trimming a single error report can be many MB.
const MAX_CONTEXT_LINE_LENGTH = 500;

const trimContextLine = (line: string, column: number | null) => {
    if (line.length <= MAX_CONTEXT_LINE_LENGTH) {
        return line;
    }
    // Keep the code around the error column when known, otherwise the start of the line
    const start = column ? Math.max(0, column - 1 - MAX_CONTEXT_LINE_LENGTH / 2) : 0;
    return line.substring(start, start + MAX_CONTEXT_LINE_LENGTH);
};

const trimStackContext = (errorReport: TraceKit.StackTrace) => {
    errorReport.stack?.forEach(frame => {
        if (frame.context) {
            // Index of the frame's line within the context, as calculated by TraceKit's gatherContext().
            // linesOfContext is a number, it is incorrectly declared as a boolean in tracekit.d.ts
            const linesBefore = Math.floor((TraceKit.linesOfContext as unknown as number) / 2);
            const frameLineIndex = Math.min(frame.line - 1, linesBefore);
            frame.context = frame.context.map((line, index) =>
                trimContextLine(line, index === frameLineIndex ? frame.column : null)
            );
        }
    });
};

TraceKit.report.subscribe(errorReport => {
    trimStackContext(errorReport);
    axios.post('/customer/v1/errors', errorReport);
});


// Setup axios
(function() {
    // console.log("Setting up axios");
    const apiBaseURL = API.ORIGIN;
    if (apiBaseURL) {
        // console.log("Setting API baseURL to ", apiBaseURL);
        axios.defaults.baseURL = apiBaseURL;
        axios.defaults.withCredentials = true;
    }

    // Unauthorised Interceptor
    axios.interceptors.response.use(function (response) {
        // Nothing to do here.
        return response;
    }, function (error) {
        // Logout when unauthorised response status is received
        if (error.response && error.response.status === 401) {
            store.dispatch(changeUser(null));
        }
        return Promise.reject(error);
    });

    // Selected Customer Interceptor
    axios.interceptors.request.use((config) => {
        const state = store.getState();
        if (state && state.auth && state.auth.selectedCustomer && state.auth.selectedCustomer.id) {
            config.headers['CustomerId'] = state.auth.selectedCustomer.id;
        }
        return config;
    })
})();

let container: HTMLElement | null = null;

const mountApp = () => {
    if (!container) {
        document.body.classList.add(isTouchDevice() ? "has-touch" : "no-touch");
        container = document.getElementById('root');
        const root = createRoot(container!);
        root.render(
            <React.StrictMode>
                <Provider store={store}>
                    <CookiesProvider defaultSetOptions={{ path: '/' }}>
                     <App />
                    </CookiesProvider>
                </Provider>
            </React.StrictMode>
        );
    }
};

try {
    CustomerService.getMe()
        .then((response) => {
            store.dispatch(changeUser(response.data));
            mountApp();
        })
        .catch((error) => {
            if (error.response && error.response.status) {
                switch (error.response.status) {
                    case 401:
                    case 403:
                        store.dispatch(changeUser(null));
                        mountApp();
                        break;
                    // case 429: // rate limited
                    //     mountApp();
                    //     return;
                    default:
                        mountApp();
                }
            }
            mountApp();
        });
} catch (e: any) {
    TraceKit.report(e); //error with stack trace gets normalized and sent to subscriber
    throw e;
}
